import {
  logAssignToMatterClickedMetric,
  logSaveToMatterClickedMetric,
  SaveToMatterClickSource,
} from "../helpers/MetricsHelper";
import { getFirmIdAsync, getTokenFromStorage } from "./authenticator-service";
import { ensureConfig } from "./configuration-service";
import { showDialog } from "./dialog-service";
import { getMatterById } from "./matter-retriever";

interface CustomProperty {
  key: string;
  value: string;
}

export interface OperationResult<T> {
  Result: T | undefined;
  Error: string | undefined;
}

export enum SaveDocumentToMatterStatus {
  Succeed = "0",
  MissingProperties = "100",
  TechnicalError = "200",
  MatterNotFound = "300",
  FirmNotFound = "400",
  FileIdNotFound = "500",
  NotLoggedIn = "600",
}

export async function getDocumentProperties(): Promise<Record<string, string>> {
  const customProperties: Record<string, string> = {};

  await Word.run(async (context) => {
    console.log(`Loading custom properties from Word document`);

    const properties = context.document.properties.customProperties;
    properties.load("items");
    await context.sync();

    properties.items.forEach((property: CustomProperty) => {
      customProperties[property.key] = property.value;
    });
  });

  return customProperties;
}

export async function setDocumentProperties(properties: Record<string, string>): Promise<void> {
  await Word.run(async (context) => {
    console.log(`Saving custom properties to Word document`);

    const customProperties = context.document.properties.customProperties;
    customProperties.load("items");
    await context.sync();

    Object.entries(properties).map(([key, value]) => {
      const prop = customProperties.items.find((cp) => cp.key === key) ?? customProperties.add(key, value);
      prop.value = value;
    });

    await context.sync();
    console.log(`Custom properties updated successfully`);
  });
}

function extractDocumentAsBlob(): Promise<Blob> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(Office.FileType.Compressed, {}, (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(`Failed to get file: ${result.error.message}`));
      }

      const file = result.value;
      const totalSlices = file.sliceCount;
      const slicesData: Uint8Array[] = [];

      const getSlice = (sliceIndex: number): void => {
        file.getSliceAsync(sliceIndex, (sliceResult) => {
          if (sliceResult.status === Office.AsyncResultStatus.Failed) {
            file.closeAsync();
            reject(new Error(`Failed to get slice #${sliceIndex.toString()}: ${sliceResult.error.message}`));
          }

          const sliceData = sliceResult.value.data as Uint8Array;
          slicesData.push(new Uint8Array(sliceData));

          if (sliceIndex === totalSlices - 1) {
            const combinedData = new Blob(slicesData, { type: "application/octet-stream" });
            file.closeAsync();
            resolve(combinedData);
          } else {
            getSlice(sliceIndex + 1);
          }
        });
      };

      getSlice(0);
    });
  });
}

async function putDocumentBlobToStorage(url: string, fileName: string, documentBlob: Blob): Promise<void> {
  //Type has to be empty so the content type of the request is empty.
  //US Live failed to save unless content type is empty. Everywhere else was accepting the previous content type, and they all support empty content type
  const file = new File([documentBlob], fileName, { type: "" });

  const response = await fetch(url, {
    method: "PUT",
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status: ${response.status.toString()} - ${response.statusText}`);
  }
}

function toFileName(fileUrl: string): string {
  const isRemoteFile = fileUrl.toLowerCase().startsWith("http");
  let separator = "/";
  if (!isRemoteFile) {
    const contextInfo = Office.context.diagnostics;
    const platformType: Office.PlatformType = contextInfo.platform;
    if (platformType === Office.PlatformType.PC || platformType === Office.PlatformType.Universal) {
      separator = "\\";
    }
  }
  const separatorIndex = fileUrl.lastIndexOf(separator);
  return fileUrl.substring(separatorIndex + 1);
}

async function getFileUrl(): Promise<string> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFilePropertiesAsync(function (asyncResult) {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded)
      {
        resolve(asyncResult.value.url);
      }
      else
      {
        reject(new Error(asyncResult.error.message));        
      }      
    });
  });

}

interface SaveToMatterResponse {
  OneDrive: {
    Failed: boolean;
    ErrorDetails: string | null;
  };
  MatterFileApi: {
    Failed: boolean;
    ErrorDetails: string | null;
  };
  S3: {
    Failed: boolean;
    ErrorDetails: string | null;
  };
  MatterFileApiResponse: {
    UploadUrl: string;
    FileId: string;
  };
}

const isOneDriveAccessFailed = (data: SaveToMatterResponse): boolean => data.OneDrive.Failed;
const isS3UploadFailed = (data: SaveToMatterResponse): boolean =>
  !data.OneDrive.Failed && !data.MatterFileApi.Failed && data.S3.Failed;
const isSuccess = (data: SaveToMatterResponse): boolean =>
  !data.OneDrive.Failed && !data.MatterFileApi.Failed && !data.S3.Failed;
const getSaveToMatterError = (data: SaveToMatterResponse): string =>
  data.OneDrive.ErrorDetails ?? data.MatterFileApi.ErrorDetails ?? data.S3.ErrorDetails ?? "";

async function doSaveToMatterAsync(
  accountId: string,
  matterId: string,
  matterFileId?: string
): Promise<OperationResult<string>> {
  const fileUrl = await getFileUrl();
  const encodedFileUrl = encodeURIComponent(fileUrl);
  const encodedFileName = encodeURIComponent(toFileName(fileUrl));
  const documentKey = `${matterId}@${fileUrl}`;

  if (matterFileId) localStorage.setItem(documentKey, matterFileId);

  const fileId = localStorage.getItem(documentKey);
  const regionConfig = ensureConfig();

  // Construct URL and parameters based on presence of fileId
  const action = fileId ? "UpdateFromOneDriveUrl" : "AddFromOneDriveUrl";
  const param = fileId ? `&fileId=${fileId}` : "";
  const url = `${regionConfig.MvcBaseUrl}/ContentDelivery/ManageTemplates/MatterFiles/${action}?AccountId=${accountId}&MatterId=${matterId}${param}&fileName=${encodedFileName}&fileUrl=${encodedFileUrl}`;

  const token = await getTokenFromStorage(regionConfig.CognitoUrl, regionConfig.CognitoClientId);
  const result: OperationResult<string> = { Result: undefined, Error: undefined };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${token?.idtoken ?? ""}` },
    });

    if (!response.ok) {
      console.error(response);
      result.Error = `${response.status.toString()} - ${response.statusText}`;
      return result;
    }

    const data = (await response.json()) as SaveToMatterResponse;

    if (isSuccess(data)) {
      result.Result = data.MatterFileApiResponse.FileId;
      localStorage.setItem(documentKey, result.Result);
      return result;
    }

    return await handleFailedSave(data, toFileName(fileUrl));
  } catch (error) {
    console.error(error);
    result.Error = `Unexpected error: ${(error as Error).message}`;
    return result;
  }
}

// Handle failed save to matter cases
async function handleFailedSave(data: SaveToMatterResponse, fileName: string): Promise<OperationResult<string>> {
  const url = data.MatterFileApiResponse.UploadUrl;

  if (isOneDriveAccessFailed(data) || isS3UploadFailed(data)) {
    try {
      const documentBlob = await extractDocumentAsBlob();
      await putDocumentBlobToStorage(url, fileName, documentBlob);
      console.log("Document uploaded successfully.");

      return { Result: data.MatterFileApiResponse.FileId, Error: undefined };
    } catch (error) {
      console.error("Document upload error:", error);
      return { Result: undefined, Error: (error as Error).message };
    }
  }

  return { Result: undefined, Error: getSaveToMatterError(data) };
}

export const showMessage = async (errorCode: SaveDocumentToMatterStatus): Promise<SaveDocumentToMatterStatus> => {
  await showDialog(`/addDocumentToMatter/addDocumentToMatter.html?errorCode=${errorCode}`, {
    height: 20,
    width: 30,
    promptBeforeOpen: false,
    displayInIframe: true,
  });
  return errorCode;
};

export async function saveToMatterAsync(source: SaveToMatterClickSource): Promise<SaveDocumentToMatterStatus> {
  try {
    await Word.run(async (context) => {
      context.document.save();
      await context.sync();
    });

    const accountId = await getFirmIdAsync();
    const { MatterId: matterId, MatterFileId: matterFileId, ...otherProps } = await getDocumentProperties();

    if (!matterId || !accountId) return await showMessage(SaveDocumentToMatterStatus.MissingProperties);
    if (!matterFileId) return SaveDocumentToMatterStatus.FileIdNotFound;

    const { matter } = await getMatterById(matterId);
    if (!matter) return await showMessage(SaveDocumentToMatterStatus.MatterNotFound);

    const result = await doSaveToMatterAsync(accountId, matterId, matterFileId);

    if (result.Error) {
      return await showMessage(SaveDocumentToMatterStatus.TechnicalError);
    }

    const addresseeName = otherProps.AddresseeName1;
    const automatedDocumentId = otherProps.AutomatedDocumentId;
    void logSaveToMatterClickedMetric(matterFileId, automatedDocumentId, source, addresseeName);

    // Do not show message on success
    return SaveDocumentToMatterStatus.Succeed;
  } catch (error) {
    console.error("Save to matter error:", error);
    return await showMessage(SaveDocumentToMatterStatus.TechnicalError);
  }
}

export async function assignToMatterAsync(matterId: string): Promise<SaveDocumentToMatterStatus> {
  try {
    await Word.run(async (context) => {
      context.document.save();
      await context.sync();
    });

    const accountId = await getFirmIdAsync();
    if (!accountId) return await showMessage(SaveDocumentToMatterStatus.FirmNotFound);

    const result = await doSaveToMatterAsync(accountId, matterId);
    if (result.Error) return await showMessage(SaveDocumentToMatterStatus.TechnicalError);

    const existingProperties = await getDocumentProperties();

    const newProperties = {
      MatterId: matterId,
      MatterFileId: result.Result ?? "",
      ParentFolderId: "",
      MatterFileProviderId: "SmokeballDocuments.WordFileOpener",
    };

    await setDocumentProperties(newProperties);

    void logAssignToMatterClickedMetric(newProperties.MatterFileId, existingProperties.AutomatedDocumentId);

    // Do not show message on success
    return SaveDocumentToMatterStatus.Succeed;
  } catch (error) {
    console.error("Assign to matter error:", error);
    return await showMessage(SaveDocumentToMatterStatus.TechnicalError);
  }
}
